import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaClock, 
  FaUsers, 
  FaRupeeSign,
  FaCheckCircle,
  FaHourglassHalf,
  FaTimesCircle,
  FaPhone,
  FaEnvelope,
  FaHome,
  FaDownload,
  FaComments,
  FaBed,
  FaBath,
  FaWifi,
  FaParking,
  FaUtensils,
  FaSwimmingPool,
  FaDumbbell,
  FaSpa,
  FaCoffee,
  FaTv,
  FaSnowflake,
  FaShieldAlt,
  FaInfoCircle,
  FaStar,
  FaPaperPlane,
  FaCheck
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { getPropertyImage } from '../utils/imageHelper';
import { downloadBookingReceipt } from '../utils/bookingReceipt';
import ReceiptPreviewModal from '../components/ReceiptPreviewModal';
import { sendGuestToHostMessage, getGuestConversation, updateGuestSentMessageStatus } from '../services/messagingService';
import { getApiUrl } from '../config/api';
import './BookingDetailsPage.css';

const CANCELLATION_REASONS = [
  { value: '', label: 'Select a reason...' },
  { value: 'change_of_plans', label: 'Change of plans' },
  { value: 'found_alternative', label: 'Found alternative accommodation' },
  { value: 'travel_dates_changed', label: 'Travel dates changed' },
  { value: 'unexpected_circumstances', label: 'Unexpected circumstances' },
  { value: 'property_not_suitable', label: 'Property no longer suitable' },
  { value: 'duplicate_booking', label: 'Duplicate booking' },
  { value: 'other', label: 'Other (please specify below)' }
];

const SUPPORT_PHONE = '+91-1800-123-4567';
const SUPPORT_EMAIL = 'support@bookandstay.com';

const transformApiBooking = (apiBooking) => {
  if (!apiBooking) return null;
  const userName = apiBooking.user?.name || '';
  const nameParts = userName.trim().split(/\s+/);
  const firstName = nameParts[0] || 'Guest';
  const lastName = nameParts.slice(1).join(' ') || '';
  return {
    bookingId: apiBooking._id,
    status: apiBooking.status,
    property: {
      _id: apiBooking.property?._id,
      name: apiBooking.property?.name || 'Property',
      city: apiBooking.property?.city || '',
      state: apiBooking.property?.state || 'Karnataka',
      type: apiBooking.property?.type || 'Hotel',
      image: apiBooking.property?.images?.[0] || apiBooking.property?.image,
      images: apiBooking.property?.images || [],
      pricePerNight: apiBooking.property?.pricePerNight,
      hostId: apiBooking.property?.hostId?._id || apiBooking.property?.hostId,
      hostEmail: apiBooking.property?.hostId?.email,
      hostName: apiBooking.property?.hostId?.name,
      rating: apiBooking.property?.rating,
      amenities: apiBooking.property?.amenities || [],
      policies: apiBooking.property?.policies || {}
    },
    checkIn: apiBooking.checkIn,
    checkOut: apiBooking.checkOut,
    guests: apiBooking.guests?.adults || 1,
    guestsDetail: apiBooking.guests,
    totalPrice: apiBooking.totalPrice,
    currency: apiBooking.currency || 'INR',
    arrivalTime: apiBooking.arrivalTime,
    specialRequests: apiBooking.specialRequests,
    companySnapshot: apiBooking.companySnapshot || null,
    staffDetails: apiBooking.staffDetails || null,
    guestInfo: {
      firstName,
      lastName,
      email: apiBooking.user?.email || '',
      phone: apiBooking.user?.phoneNumber || apiBooking.user?.phone || ''
    },
    createdAt: apiBooking.createdAt,
    cancelReason: apiBooking.cancelReason,
    cancelledAt: apiBooking.cancelledAt,
    paymentMethod: apiBooking.paymentMethod || 'payAtHotel',
    paymentStatus: apiBooking.paymentStatus || null
  };
};

const BookingDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(location.state?.booking || null);
  const [loading, setLoading] = useState(!location.state?.booking);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReasonType, setCancelReasonType] = useState('');
  const [cancelReasonCustom, setCancelReasonCustom] = useState('');
  const [cancelDetails, setCancelDetails] = useState('');
  const [agreeToPolicy, setAgreeToPolicy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const chatBodyRef = useRef(null);
  // Review section: user's review for this booking's property
  const [myReview, setMyReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) setCurrentUser(JSON.parse(storedUser));
    } catch (e) {
      console.error('Error loading user:', e);
    }
  }, []);

  // Fetch current user's review for this booking's property
  useEffect(() => {
    if (!booking?.property?._id || !currentUser) {
      setMyReview(null);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setMyReview(null);
      return;
    }
    const propertyId = booking.property._id;
    const fetchMyReview = async () => {
      setReviewLoading(true);
      try {
        const res = await fetch(getApiUrl('/api/reviews/mine'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.reviews)) {
          const forThisProperty = data.reviews.find(
            (r) => String(r.propertyId) === String(propertyId)
          );
          setMyReview(forThisProperty || null);
        } else {
          setMyReview(null);
        }
      } catch (e) {
        console.error('Error fetching my review:', e);
        setMyReview(null);
      } finally {
        setReviewLoading(false);
      }
    };
    fetchMyReview();
  }, [booking?.property?._id, currentUser]);

  useEffect(() => {
    const loadBookingDetails = async () => {
      if (!bookingId) return;
      const token = localStorage.getItem('token');
      if (!token) {
        if (location.state?.booking && location.state.booking.bookingId === bookingId) {
          setBooking(location.state.booking);
        } else {
          setError('Please log in to view booking details');
          setBooking(null);
        }
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(getApiUrl(`/api/bookings/${bookingId}`), {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const result = await response.json();
        if (result.success && result.booking) {
          const transformed = transformApiBooking(result.booking);
          setBooking(transformed);
        } else if (location.state?.booking && location.state.booking.bookingId === bookingId) {
          setBooking(location.state.booking);
        } else {
          setError(result.message || 'Booking not found');
          setBooking(null);
        }
      } catch (e) {
        console.error('Error fetching booking:', e);
        if (location.state?.booking && location.state.booking.bookingId === bookingId) {
          setBooking(location.state.booking);
        } else {
          setError('Failed to load booking details');
          setBooking(null);
        }
      } finally {
        setLoading(false);
      }
    };
    loadBookingDetails();
  }, [bookingId, location.state]);

  // Load conversation when message modal is opened or booking changes
  useEffect(() => {
    if (showMessageModal && booking) loadConversation(booking);
  }, [showMessageModal, booking]);

  // Listen to storage and custom events to refresh messages
  useEffect(() => {
    const handleStorage = () => {
      if (showMessageModal && booking) loadConversation(booking);
    };
    const handleMessagesUpdated = () => {
      if (showMessageModal && booking) loadConversation(booking);
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('messagesUpdated', handleMessagesUpdated);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('messagesUpdated', handleMessagesUpdated);
    };
  }, [showMessageModal, booking]);

  // Poll for host replies when message modal is open (20s to reduce API rate limit pressure)
  useEffect(() => {
    if (!showMessageModal || !booking) return;
    const interval = setInterval(() => {
      loadConversation(booking);
    }, 20000);
    return () => clearInterval(interval);
  }, [showMessageModal, booking]);

  // Auto scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatBodyRef.current && conversationMessages.length > 0) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [conversationMessages]);

  const loadConversation = useCallback(async (b) => {
    if (!b) return;
    const userId = currentUser?._id || currentUser?.id || 'demo_user';
    const hostId = b.property?.hostId?._id ?? b.property?.hostId ?? 'host_demo';
    try {
      const messages = await getGuestConversation(userId, hostId, b.bookingId);
      setConversationMessages(messages);
    } catch (e) {
      setConversationMessages([]);
    }
  }, [currentUser]);

  useEffect(() => {
    if (showMessageModal && booking) loadConversation(booking);
  }, [showMessageModal, booking, loadConversation]);

  useEffect(() => {
    const handleStorage = () => {
      if (showMessageModal && booking) loadConversation(booking);
    };
    const handleMessagesUpdated = () => {
      if (showMessageModal && booking) loadConversation(booking);
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('messagesUpdated', handleMessagesUpdated);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('messagesUpdated', handleMessagesUpdated);
    };
  }, [showMessageModal, booking, loadConversation]);

  useEffect(() => {
    if (!showMessageModal || !booking) return;
    const interval = setInterval(() => {
      loadConversation(booking);
    }, 20000);
    return () => clearInterval(interval);
  }, [showMessageModal, booking, loadConversation]);

  useEffect(() => {
    if (chatBodyRef.current && conversationMessages.length > 0) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [conversationMessages]);

  if (loading && !booking) {
    return (
      <div className="booking-details-page">
        <Navbar />
        <div className="booking-details-container">
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading booking details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="booking-details-page">
        <Navbar />
        <div className="booking-details-container">
          <div className="error-state">
            <FaInfoCircle className="error-icon" />
            <h2>Booking Not Found</h2>
            <p>{error}</p>
            <button className="btn-back" onClick={() => navigate('/trips')}>
              <FaArrowLeft /> Back to Trips
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="booking-details-page">
        <Navbar />
        <div className="booking-details-container">
          <div className="error-state">
            <FaInfoCircle className="error-icon" />
            <h2>Booking Not Found</h2>
            <p>The booking you're looking for doesn't exist or has been removed.</p>
            <button className="btn-back" onClick={() => navigate('/trips')}>
              <FaArrowLeft /> Back to Trips
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: <FaHourglassHalf />, text: 'Pending Approval', class: 'pending' },
      confirmed: { icon: <FaCheckCircle />, text: 'Booking Confirmed', class: 'approved' },
      approved: { icon: <FaCheckCircle />, text: 'Booking Confirmed', class: 'approved' },
      declined: { icon: <FaTimesCircle />, text: 'Declined', class: 'declined' },
      cancelled: { icon: <FaTimesCircle />, text: 'Cancelled', class: 'declined' }
    };
    return badges[status] || badges.pending;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatFullDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateNights = (checkIn, checkOut) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const handleDownload = () => {
    setShowReceiptModal(true);
  };

  const handleMessageHost = () => {
    setShowMessageModal(true);
    loadConversation(booking);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    setSendingMessage(true);
    try {
      const userId = currentUser?._id || currentUser?.id || 'demo_user';
      const hostIdRaw = booking.property?.hostId?._id ?? booking.property?.hostId;
      const hostId = hostIdRaw != null ? String(hostIdRaw) : null;
      if (!hostId) {
        setSendingMessage(false);
        toast.error('Could not determine host. Please refresh the page.');
        return;
      }
      const guestName = currentUser?.name || `${booking.guestInfo?.firstName || ''} ${booking.guestInfo?.lastName || ''}`.trim() || 'Guest';
      const result = await sendGuestToHostMessage({
        guestId: userId,
        hostId,
        bookingId: booking.bookingId,
        propertyName: booking.property?.name || 'Property',
        message: messageText,
        guestName
      });
      if (result.success) {
        setMessageText('');
        loadConversation(booking);
        setTimeout(() => {
          updateGuestSentMessageStatus(result.message.id, 'delivered');
          loadConversation(booking);
        }, 1500);
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (e) {
      console.error('Error sending message:', e);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const supportPhoneLink = `tel:${SUPPORT_PHONE.replace(/\s|-/g, '')}`;
  const hostEmail = booking?.property?.hostEmail || booking?.property?.hostId?.email;
  const emailTo = hostEmail || SUPPORT_EMAIL;
  const emailSubject = encodeURIComponent(`Booking Inquiry - ${booking?.property?.name || 'Property'} (${booking?.bookingId || ''})`);
  const emailBody = encodeURIComponent(`Hello,\n\nI have a question about my booking:\nBooking ID: ${booking?.bookingId || ''}\nProperty: ${booking?.property?.name || ''}\n\n`);
  const emailHostLink = `mailto:${emailTo}?subject=${emailSubject}&body=${emailBody}`;

  const getCancelReasonForSubmit = () => {
    if (cancelReasonType === 'other') return cancelReasonCustom.trim();
    const selected = CANCELLATION_REASONS.find(r => r.value === cancelReasonType);
    return selected ? selected.label : '';
  };

  const isCancelReasonValid = () => {
    if (cancelReasonType === 'other') return cancelReasonCustom.trim().length > 0;
    return cancelReasonType && cancelReasonType !== '';
  };

  const handleCancelBooking = async () => {
    const reason = getCancelReasonForSubmit();
    if (!reason) {
      toast.warning('Please select a cancellation reason or provide details for "Other"');
      return;
    }
    if (!agreeToPolicy) {
      toast.warning('Please agree to the cancellation policy');
      return;
    }
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/bookings/stay/${booking.bookingId}/cancel`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });
      const result = await response.json();
      if (result.success) {
        toast.success('Booking cancelled successfully! Refund will be processed according to the policy.');
        setShowCancelModal(false);
        navigate('/trips');
      } else {
        toast.error(result.message || 'Failed to cancel booking. Please try again.');
      }
    } catch (e) {
      console.error('Error cancelling:', e);
      toast.error('Failed to cancel booking. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const MessageTicks = ({ status }) => {
    if (status === 'read') return <span className="chat-ticks chat-ticks--read" title="Seen"><FaCheck /><FaCheck /></span>;
    if (status === 'delivered') return <span className="chat-ticks chat-ticks--delivered" title="Delivered"><FaCheck /><FaCheck /></span>;
    return <span className="chat-ticks chat-ticks--sent" title="Sent"><FaCheck /></span>;
  };

  const calculateRefund = () => {
    const checkInDate = new Date(booking.checkIn);
    const today = new Date();
    const daysUntilCheckIn = Math.ceil((checkInDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilCheckIn >= 7) {
      return { percentage: 100, amount: booking.totalPrice };
    } else if (daysUntilCheckIn >= 3) {
      return { percentage: 50, amount: booking.totalPrice * 0.5 };
    } else if (daysUntilCheckIn >= 1) {
      return { percentage: 25, amount: booking.totalPrice * 0.25 };
    } else {
      return { percentage: 0, amount: 0 };
    }
  };

  const isStayOver = () => {
    const checkout = new Date(booking.checkOut);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    checkout.setHours(0, 0, 0, 0);
    return checkout <= today;
  };

  const canLeaveReview = () => {
    if (!booking || !currentUser) return false;
    if (booking.status !== 'confirmed' && booking.status !== 'approved') return false;
    if (booking.status === 'cancelled') return false;
    return isStayOver();
  };

  const handleSubmitReview = async () => {
    const propertyId = booking?.property?._id;
    if (!propertyId || !currentUser || reviewRating < 1 || reviewSubmitting) return;
    const token = localStorage.getItem('token');
    if (!token) {
      toast.warning('Please log in to leave a review.');
      return;
    }
    setReviewSubmitting(true);
    try {
      const res = await fetch(getApiUrl(`/api/properties/${propertyId}/reviews`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rating: reviewRating, comment: reviewComment.trim() }),
      });
      const data = await res.json();
      if (data.success && data.review) {
        setMyReview({
          _id: data.review._id,
          rating: data.review.rating,
          comment: data.review.comment,
          guestName: data.review.guestName,
          hostReply: data.review.hostReply || '',
          hostRepliedAt: data.review.hostRepliedAt || null,
          createdAt: data.review.createdAt,
        });
        setReviewRating(0);
        setReviewComment('');
        toast.success(data.message || 'Thank you for your review!');
      } else {
        toast.error(data.message || 'Failed to submit review.');
      }
    } catch (e) {
      console.error('Submit review error:', e);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const statusBadge = getStatusBadge(booking.status);
  const nights = calculateNights(booking.checkIn, booking.checkOut);

  // Amenities from property or default list
  const amenityIconMap = {
    wifi: <FaWifi />,
    parking: <FaParking />,
    kitchen: <FaUtensils />,
    tv: <FaTv />,
    ac: <FaSnowflake />,
    pool: <FaSwimmingPool />,
    gym: <FaDumbbell />,
    spa: <FaSpa />,
    coffee: <FaCoffee />
  };
  const propertyAmenities = booking.property?.amenities || [];
  const amenities = propertyAmenities.length > 0
    ? propertyAmenities.slice(0, 8).map(a => {
        const key = (a || '').toLowerCase();
        const icon = Object.keys(amenityIconMap).find(k => key.includes(k));
        return { icon: icon ? amenityIconMap[icon] : <FaCheck />, name: a };
      })
    : [
        { icon: <FaWifi />, name: 'Free WiFi' },
        { icon: <FaParking />, name: 'Free Parking' },
        { icon: <FaUtensils />, name: 'Kitchen' },
        { icon: <FaTv />, name: 'TV' },
        { icon: <FaSnowflake />, name: 'AC' },
        { icon: <FaSwimmingPool />, name: 'Pool' }
      ];

  return (
    <div className="booking-details-page">
      <Navbar />
      
      <div className="booking-details-container">
        {/* Compact Header */}
        <div className="details-header-compact">
          <button className="btn-back-compact" onClick={() => navigate('/trips')}>
            <FaArrowLeft /> Back
          </button>
          <div className="header-info">
            <span className="booking-id-compact">#{booking.bookingId}</span>
            <div className={`status-badge-compact ${statusBadge.class}`}>
              {statusBadge.icon}
              <span>{statusBadge.text}</span>
            </div>
          </div>
          <button className="btn-download-compact" onClick={handleDownload}>
            <FaDownload /> Download
          </button>
        </div>

        {showReceiptModal && booking && (
          <ReceiptPreviewModal
            booking={booking}
            onClose={() => setShowReceiptModal(false)}
            onDownload={downloadBookingReceipt}
          />
        )}

        {/* Main Content Grid */}
        <div className="details-grid">
          {/* Left Column */}
          <div className="details-left">
            {/* Property Card */}
            <div className="property-card-compact">
              <div className="property-image-compact">
                <img
                  src={getPropertyImage(booking.property)}
                  alt={booking.property.name}
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80';
                  }}
                />
                {booking.property.rating && (
                  <div className="rating-badge">
                    <FaStar /> {booking.property.rating}
                  </div>
                )}
              </div>
              <div className="property-info-compact">
                <h1>{booking.property.name}</h1>
                <p className="location-compact">
                  <FaMapMarkerAlt /> {booking.property.city}, {booking.property.state}
                </p>
                {booking.property.type && (
                  <span className="property-type-badge">{booking.property.type}</span>
                )}
              </div>
            </div>

            {/* Booking Timeline */}
            <div className="timeline-card">
              <h3>Your Stay</h3>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-icon checkin">
                    <FaCalendarAlt />
                  </div>
                  <div className="timeline-content">
                    <span className="timeline-label">Check-in</span>
                    <span className="timeline-date">{formatFullDate(booking.checkIn)}</span>
                    <span className="timeline-time">After 3:00 PM</span>
                  </div>
                </div>
                <div className="timeline-line">
                  <span className="timeline-duration">{nights} {nights === 1 ? 'Night' : 'Nights'}</span>
                </div>
                <div className="timeline-item">
                  <div className="timeline-icon checkout">
                    <FaCalendarAlt />
                  </div>
                  <div className="timeline-content">
                    <span className="timeline-label">Check-out</span>
                    <span className="timeline-date">{formatFullDate(booking.checkOut)}</span>
                    <span className="timeline-time">Before 12:00 PM</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="amenities-card">
              <h3>Amenities</h3>
              <div className="amenities-grid">
                {amenities.map((amenity, index) => (
                  <div key={index} className="amenity-item">
                    {amenity.icon}
                    <span>{amenity.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Important Information */}
            <div className="info-card">
              <h3>Important Information</h3>
              <div className="info-list">
                <div className="info-list-item">
                  <FaShieldAlt className="info-icon" />
                  <div>
                    <strong>Valid ID Required</strong>
                    <p>Please bring a government-issued photo ID at check-in</p>
                  </div>
                </div>
                <div className="info-list-item">
                  <FaRupeeSign className="info-icon" />
                  <div>
                    <strong>{booking.paymentMethod === 'online' && booking.paymentStatus === 'paid' ? 'Paid online' : 'Payment at Hotel'}</strong>
                    <p>
                      {booking.paymentMethod === 'online' && booking.paymentStatus === 'paid'
                        ? 'Payment completed online. No payment due at check-in.'
                        : 'Full payment due at check-in (Cash, Card, or UPI accepted)'}
                    </p>
                  </div>
                </div>
                <div className="info-list-item">
                  <FaInfoCircle className="info-icon" />
                  <div>
                    <strong>Cancellation Policy</strong>
                    <p>{booking.property?.policies?.cancellationPolicy || 'Free cancellation up to 24 hours before check-in'}</p>
                  </div>
                </div>
                {booking.specialRequests && (
                  <div className="info-list-item">
                    <FaInfoCircle className="info-icon" />
                    <div>
                      <strong>Special Requests</strong>
                      <p>{booking.specialRequests}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="details-right">
            {/* Booking Summary */}
            <div className="summary-card">
              <h3>Booking Summary</h3>
              <div className="summary-row">
                <span><FaUsers /> Guests</span>
                <span className="summary-value">
                  {booking.guestsDetail?.adults || booking.guests || 1} Adult{(booking.guestsDetail?.adults || booking.guests || 1) !== 1 ? 's' : ''}
                  {(booking.guestsDetail?.children || 0) > 0 && `, ${booking.guestsDetail.children} ${(booking.guestsDetail.children || 0) === 1 ? 'Child' : 'Children'}`}
                  {(booking.guestsDetail?.pets || 0) > 0 && `, ${booking.guestsDetail.pets} Pet${(booking.guestsDetail.pets || 0) !== 1 ? 's' : ''}`}
                </span>
              </div>
              <div className="summary-row">
                <span><FaClock /> Duration</span>
                <span className="summary-value">{nights} {nights === 1 ? 'Night' : 'Nights'}</span>
              </div>
              {booking.arrivalTime && (
                <div className="summary-row">
                  <span><FaClock /> Arrival</span>
                  <span className="summary-value">{booking.arrivalTime}</span>
                </div>
              )}
              <div className="summary-divider"></div>
              <div className="summary-row total">
                <span>Total Amount</span>
                <span className="summary-total">₹{booking.totalPrice?.toLocaleString()}</span>
              </div>
              <div className={`payment-status-box ${(booking.status === 'confirmed' || booking.status === 'approved') ? 'confirmed' : ''}`}>
                <FaInfoCircle />
                <span>
                  {booking.status === 'cancelled'
                    ? 'Booking cancelled'
                    : booking.paymentMethod === 'online' && booking.paymentStatus === 'paid'
                    ? (booking.status === 'confirmed' || booking.status === 'approved' ? 'Booking confirmed - Paid online' : 'Paid online - Awaiting host confirmation')
                    : (booking.status === 'confirmed' || booking.status === 'approved')
                    ? 'Booking confirmed - Pay at hotel at check-in'
                    : 'Payment pending - Pay at hotel at check-in'}
                </span>
              </div>
            </div>

            {/* Guest Details */}
            {booking.guestInfo && (
              <div className="guest-card">
                <h3>Guest Details</h3>
                {booking.companySnapshot?.name && (
                  <div className="guest-detail-row">
                    <span>Company</span>
                    <span>{booking.companySnapshot.name}</span>
                  </div>
                )}
                {booking.staffDetails?.name && (
                  <>
                    <div className="guest-detail-row">
                      <span>Booked By</span>
                      <span>{booking.staffDetails.name}</span>
                    </div>
                    {booking.staffDetails.role && (
                      <div className="guest-detail-row">
                        <span>Staff Role</span>
                        <span>{booking.staffDetails.role}</span>
                      </div>
                    )}
                    {booking.staffDetails.employeeId && (
                      <div className="guest-detail-row">
                        <span>Employee ID</span>
                        <span>{booking.staffDetails.employeeId}</span>
                      </div>
                    )}
                    {booking.staffDetails.mobileNumber && (
                      <div className="guest-detail-row">
                        <span>Mobile</span>
                        <span>{booking.staffDetails.mobileNumber}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="guest-detail-row">
                  <span>Name</span>
                  <span>{booking.guestInfo.firstName} {booking.guestInfo.lastName}</span>
                </div>
                <div className="guest-detail-row">
                  <span>Email</span>
                  <span>{booking.guestInfo.email}</span>
                </div>
                {booking.guestInfo.phone && (
                  <div className="guest-detail-row">
                    <span>Phone</span>
                    <span>{booking.guestInfo.phone}</span>
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions */}
            <div className="actions-card">
              <h3>Need Help?</h3>
              <button className="action-btn primary" onClick={handleMessageHost}>
                <FaComments /> Message Host
              </button>
              <a href={supportPhoneLink} className="action-btn secondary" rel="noopener noreferrer">
                <FaPhone /> Contact Support
              </a>
              <a href={emailHostLink} className="action-btn secondary" rel="noopener noreferrer">
                <FaEnvelope /> Email Host
              </a>
              {(booking.status === 'pending' || booking.status === 'confirmed' || booking.status === 'approved') && (
                <button className="action-btn danger" onClick={() => { setShowCancelModal(true); setCancelReasonType(''); setCancelReasonCustom(''); }}>
                  <FaTimesCircle /> Cancel Booking
                </button>
              )}
            </div>

            {/* Review Section */}
            <div className="booking-review-card">
              <h3><FaStar className="section-icon" /> Your Review</h3>
              {reviewLoading ? (
                <div className="booking-review-loading">
                  <span className="spinner" /> Loading...
                </div>
              ) : myReview ? (
                <div className="booking-review-display">
                  <div className="booking-review-stars">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <FaStar key={s} className={s <= myReview.rating ? 'star-filled' : 'star-empty'} />
                    ))}
                  </div>
                  {myReview.comment && <p className="booking-review-comment">{myReview.comment}</p>}
                  <span className="booking-review-date">
                    {myReview.createdAt
                      ? new Date(myReview.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                      : ''}
                  </span>
                  {myReview.hostReply && (
                    <div className="booking-review-host-reply">
                      <strong>Host reply:</strong>
                      <p>{myReview.hostReply}</p>
                      {myReview.hostRepliedAt && (
                        <span className="host-reply-date">
                          {new Date(myReview.hostRepliedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ) : canLeaveReview() ? (
                <div className="booking-review-form">
                  <p className="booking-review-prompt">How was your stay at {booking.property?.name}?</p>
                  <div className="booking-review-rating">
                    <span className="rating-label">Rating</span>
                    <div className="star-input">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button
                          key={s}
                          type="button"
                          className={`star-btn ${s <= reviewRating ? 'selected' : ''}`}
                          onClick={() => setReviewRating(s)}
                          aria-label={`${s} star${s > 1 ? 's' : ''}`}
                        >
                          <FaStar />
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea
                    className="booking-review-textarea"
                    placeholder="Share your experience (optional)"
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    rows={3}
                  />
                  <button
                    type="button"
                    className="btn-submit-booking-review"
                    onClick={handleSubmitReview}
                    disabled={reviewRating < 1 || reviewSubmitting}
                  >
                    {reviewSubmitting ? (
                      <>
                        <span className="spinner" /> Submitting...
                      </>
                    ) : (
                      'Submit Review'
                    )}
                  </button>
                </div>
              ) : (
                <div className="booking-review-upcoming">
                  {booking.status === 'cancelled' ? (
                    <p>This booking was cancelled.</p>
                  ) : !isStayOver() ? (
                    <p>You can leave a review after your stay (from check-out day).</p>
                  ) : (booking.status !== 'confirmed' && booking.status !== 'approved') ? (
                    <p>Reviews are available for confirmed stays.</p>
                  ) : (
                    <p>You can leave a review after your stay.</p>
                  )}
                  {booking.property?._id && (
                    <button
                      type="button"
                      className="btn-link-to-property"
                      onClick={() => navigate(`/property/${booking.property._id}`)}
                    >
                      View property
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Cancellation Info */}
            <div className="cancellation-card">
              <h3>Cancellation</h3>
              <div className="cancellation-info">
                <FaCheckCircle className="cancel-icon success" />
                <div>
                  <strong>Free until {formatDate(new Date(new Date(booking.checkIn).getTime() - 24 * 60 * 60 * 1000))}</strong>
                  <p>Cancel before this date for a full refund</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message Modal - Chat Style */}
      {showMessageModal && booking && (
        <div className="modal-overlay modal-overlay--chat" onClick={() => setShowMessageModal(false)}>
          <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-header">
              <div className="chat-header-info">
                <h3 className="chat-title">Message Host</h3>
                <p className="chat-subtitle">{booking.property?.name}</p>
                <span className="chat-booking-id">ID: {booking.bookingId}</span>
              </div>
              <button className="chat-close" onClick={() => setShowMessageModal(false)} aria-label="Close">×</button>
            </div>
            <div className="chat-body" ref={chatBodyRef}>
              {conversationMessages.length === 0 ? (
                <div className="chat-empty-state">
                  <FaComments className="chat-empty-icon" />
                  <p>Start a conversation with your host</p>
                  <span>Type your message below and hit send</span>
                </div>
              ) : (
                <div className="chat-messages">
                  {conversationMessages.map((msg) => (
                    <div key={msg.id} className={`chat-bubble chat-bubble--${msg.from}`}>
                      <div className="chat-bubble-content">
                        <p className="chat-bubble-text">{msg.message}</p>
                        <div className="chat-bubble-meta">
                          <span className="chat-bubble-time">{formatMessageTime(msg.timestamp)}</span>
                          {msg.from === 'guest' && <MessageTicks status={msg.status || 'sent'} />}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="chat-input-bar">
              <textarea
                className="chat-input"
                placeholder="Type a message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                rows={2}
                disabled={sendingMessage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                }}
              />
              <button
                className="chat-send-btn"
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageText.trim()}
                aria-label="Send message"
              >
                {sendingMessage ? <span className="chat-send-spinner" /> : <FaPaperPlane />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-compact">
              <h3>Cancel Booking</h3>
              <button className="btn-close-compact" onClick={() => setShowCancelModal(false)}>×</button>
            </div>
            
            <div className="modal-body-compact">
              {/* Refund Information */}
              <div className="refund-info-box">
                <div className="refund-header">
                  <FaRupeeSign className="refund-icon" />
                  <div>
                    <strong>Refund Amount</strong>
                    <p className="refund-amount">₹{calculateRefund().amount.toLocaleString()} ({calculateRefund().percentage}%)</p>
                  </div>
                </div>
              </div>

              {/* Cancellation Policy */}
              <div className="policy-section">
                <h4><FaShieldAlt /> Cancellation Policy</h4>
                <div className="policy-list">
                  <div className="policy-item">
                    <FaCheckCircle className="policy-icon success" />
                    <div>
                      <strong>7+ days before check-in</strong>
                      <p>100% refund of booking amount</p>
                    </div>
                  </div>
                  <div className="policy-item">
                    <FaCheckCircle className="policy-icon warning" />
                    <div>
                      <strong>3-6 days before check-in</strong>
                      <p>50% refund of booking amount</p>
                    </div>
                  </div>
                  <div className="policy-item">
                    <FaCheckCircle className="policy-icon partial" />
                    <div>
                      <strong>1-2 days before check-in</strong>
                      <p>25% refund of booking amount</p>
                    </div>
                  </div>
                  <div className="policy-item">
                    <FaTimesCircle className="policy-icon danger" />
                    <div>
                      <strong>Less than 24 hours</strong>
                      <p>No refund available</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cancellation Features */}
              <div className="features-section">
                <h4><FaInfoCircle /> What Happens Next</h4>
                <ul className="features-list">
                  <li><FaCheckCircle /> Instant cancellation confirmation via email</li>
                  <li><FaCheckCircle /> Refund processed within 5-7 business days</li>
                  <li><FaCheckCircle /> Host will be notified automatically</li>
                  <li><FaCheckCircle /> Booking removed from your trips</li>
                  <li><FaCheckCircle /> No hidden charges or fees</li>
                  <li><FaCheckCircle /> 24/7 customer support available</li>
                </ul>
              </div>

              {/* Cancellation Reason */}
              <div className="form-group">
                <label>Reason for Cancellation *</label>
                <select
                  className="form-select cancel-reason-select"
                  value={cancelReasonType}
                  onChange={(e) => setCancelReasonType(e.target.value)}
                  disabled={cancelling}
                >
                  {CANCELLATION_REASONS.map((r) => (
                    <option key={r.value || 'empty'} value={r.value}>{r.label}</option>
                  ))}
                </select>
                {cancelReasonType === 'other' && (
                  <textarea
                    className="form-textarea"
                    placeholder="Please specify your reason..."
                    value={cancelReasonCustom}
                    onChange={(e) => setCancelReasonCustom(e.target.value)}
                    rows="3"
                    disabled={cancelling}
                    style={{ marginTop: '12px' }}
                  />
                )}
              </div>

              {/* Additional Details */}
              <div className="form-group">
                <label>Additional Details (Optional)</label>
                <textarea
                  className="form-textarea"
                  placeholder="Tell us more about your cancellation..."
                  value={cancelDetails}
                  onChange={(e) => setCancelDetails(e.target.value)}
                  rows="2"
                  disabled={cancelling}
                />
              </div>

              {/* Agreement Checkbox */}
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="agreePolicy"
                  checked={agreeToPolicy}
                  onChange={(e) => setAgreeToPolicy(e.target.checked)}
                />
                <label htmlFor="agreePolicy">
                  I understand and agree to the cancellation policy. I acknowledge that the refund amount will be based on the time remaining until check-in.
                </label>
              </div>
            </div>

            <div className="modal-footer-compact">
              <button className="btn-modal-cancel" onClick={() => setShowCancelModal(false)}>
                Keep Booking
              </button>
              <button
                className="btn-modal-danger"
                onClick={handleCancelBooking}
                disabled={cancelling || !isCancelReasonValid() || !agreeToPolicy}
              >
                {cancelling ? 'Cancelling...' : <><FaTimesCircle /> Confirm Cancellation</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetailsPage;
